import { Icon } from '@iconify-icon/react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'what-you-get', title: '1. What MCP Setup Gives You' },
  { id: 'before-you-start', title: '2. Before You Start' },
  { id: 'run-agent-login', title: '3. Run Agent Login' },
  { id: 'approve-access', title: '4. Approve Access in Chat.co' },
  { id: 'configure-client', title: '5. Configure Your MCP Client' },
  { id: 'verify-connection', title: '6. Verify the Connection' },
  { id: 'scope-security', title: '7. Scope & Security Choices' },
  { id: 'troubleshooting', title: '8. Troubleshooting' },
];

const McpAgentSetupPage = () => {
  return (
    <>
      <NextSeo
        title="Connect Claude Desktop or Cursor to Chat.co with MCP"
        description="Set up Chat.co MCP with agent login, approve access in the browser, and connect your AI client to manage bots, content, and analytics."
        canonical="https://chat.co/docs/mcp-agent-setup"
        openGraph={{
          url: 'https://chat.co/docs/mcp-agent-setup',
          title: 'Connect Claude Desktop or Cursor to Chat.co with MCP | Chat.co Docs',
          description:
            'Set up Chat.co MCP with agent login, approve access in the browser, and connect your AI client to manage bots, content, and analytics.',
          type: 'article',
          article: {
            publishedTime: '2026-03-06T00:00:00Z',
            modifiedTime: '2026-03-06T00:00:00Z',
            section: 'Integrations',
            tags: ['mcp', 'claude desktop', 'cursor', 'agent login', 'integrations'],
          },
          images: [
            {
              url: 'https://chat.co/images/og-docs.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co MCP Setup Guide',
            },
          ],
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />

      <DocsArticleLayout
        category="Integrations"
        categoryColor="text-orange-700"
        title="Connect Claude Desktop or Cursor to Chat.co with MCP"
        description="Approve a local MCP connection to Chat.co so your AI client can work with chatbots, training content, documents, and analytics."
        readingTime="11 min read"
        lastUpdated="March 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'API vs MCP',
          href: PageLink.docsApiVsMcp,
        }}
        nextArticle={{
          title: 'Website Integration',
          href: PageLink.docsWebsiteIntegration,
        }}
        keyTakeaways={[
          'Agent login lets you approve MCP access in the browser instead of manually copying a key',
          'You can scope the connection to one chatbot or keep it unscoped for broader account tasks',
          'The MCP server reads a local key file and exposes Chat.co capabilities as tools',
          'Unscoped access is usually required for account-wide analytics and creating new chatbots',
          'Start narrow, test the workflow, then expand permissions only when needed',
        ]}>
        <p className="lead text-lg text-gray-700">
          MCP lets an AI client treat Chat.co like a tool-enabled workspace. Instead of manually
          writing API calls, you approve access once and let your client use tools for tasks like
          updating prompts, uploading documents, starting crawls, or reviewing leads.
        </p>

        <div className="my-8 rounded-xl border border-orange-200 bg-orange-50 p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-orange-100">
              <Icon icon="tabler:plug-connected" className="text-xl text-orange-600" />
            </div>
            <div>
              <h4 className="font-semibold text-orange-900">
                Start Here if You Want Agent Actions
              </h4>
              <p className="mt-1 text-orange-800">
                If you want Claude Desktop, Cursor, or another MCP-compatible client to act on your
                Chat.co workspace, this guide is the right starting point. If you want raw HTTP
                requests instead, use the{' '}
                <Link href={PageLink.docsApiReference} className="font-medium underline">
                  direct API docs
                </Link>
                .
              </p>
            </div>
          </div>
        </div>

        <section id="what-you-get" className="scroll-mt-24">
          <h2>1. What MCP Setup Gives You</h2>

          <p>
            Once connected, your AI client can use Chat.co as a set of tools rather than just a chat
            endpoint.
          </p>

          <p>
            The server exposes the full Chat.co management surface &mdash; the same endpoints
            documented in the{' '}
            <Link href={PageLink.docsApiReference} className="text-primary-600 hover:underline">
              API reference
            </Link>{' '}
            &mdash; as roughly fifty individually callable tools, grouped as follows:
          </p>

          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Chatbots &amp; configuration</h4>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li>List, inspect, create, update and delete chatbots</li>
                <li>Read the full sanitized configuration</li>
                <li>Change the instruction prompt, temperature and appearance</li>
                <li>Manage embed domains, rate limits and the email whitelist</li>
              </ul>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Knowledge</h4>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li>Upload documents, check for duplicates, delete document jobs</li>
                <li>Start, monitor and cancel crawls</li>
                <li>Review discovered pages and choose which to index</li>
                <li>Create, update and bulk-create Q&amp;A pairs and FAQs</li>
                <li>Replace or remove text training content</li>
              </ul>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Conversations</h4>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li>Create and list conversations</li>
                <li>Read a conversation with its messages</li>
                <li>Send a message and get the grounded reply</li>
              </ul>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Insight</h4>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li>Chatbot analytics and remaining message allocation</li>
                <li>Captured leads</li>
                <li>Analyse knowledge gaps, draft answers, promote them to Q&amp;A</li>
              </ul>
            </div>
          </div>

          <div className="my-6 rounded-xl border border-primary-100 bg-primary-50 p-5">
            <p className="text-sm leading-relaxed text-primary-900">
              This is what makes MCP worth the setup over raw HTTP: your client can chain these
              &mdash; analyse gaps, draft answers, create the Q&amp;A pairs, re-test &mdash; in one
              conversation, without you writing the glue.
            </p>
          </div>

          <p>
            If you want the comparison first, read{' '}
            <Link
              href={PageLink.docsApiVsMcp}
              className="font-medium text-primary-600 hover:underline">
              API vs MCP
            </Link>
            .
          </p>
        </section>

        <section id="before-you-start" className="scroll-mt-24">
          <h2>2. Before You Start</h2>

          <p>Make sure you have the following ready:</p>

          <ul>
            <li>A Chat.co account with at least one chatbot</li>
            <li>Access to an MCP-compatible client such as Claude Desktop or Cursor</li>
            <li>Node.js 18 or newer installed locally (the MCP server runs on Node)</li>
            <li>A decision about whether this connection should be scoped to one chatbot or not</li>
          </ul>

          <h3>Install the Chat.co MCP server</h3>

          <p>Install once globally:</p>

          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">{`npm install -g chatco-mcp@1.0.0`}</pre>
          </div>

          <p>
            Or skip the install and run via <code>npx</code> on demand — every command in the next
            section can be prefixed with <code>npx -y chatco-mcp@1.0.0</code> instead of the bare
            command.
          </p>

          <div className="my-6 rounded-lg border border-gray-200 bg-white p-5">
            <h4 className="font-semibold text-gray-900">Important planning choice</h4>
            <p className="mt-2 text-sm text-gray-600">
              If you want the client to create chatbots or see account-wide analytics, keep the
              connection unscoped. If you want to restrict it to one bot, plan to use a chatbot ID
              during login.
            </p>
          </div>
        </section>

        <section id="run-agent-login" className="scroll-mt-24">
          <h2>3. Run Agent Login</h2>

          <p>
            Start the approval flow from your terminal. (If you skipped the global install in
            Section 2, prefix any of the commands below with <code>npx -y</code>, e.g.{' '}
            <code>npx -y chatco-mcp@1.0.0 login ...</code>.)
          </p>

          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">{`chatco-mcp login --name "Claude Desktop"`}</pre>
          </div>

          <p>To limit access to a single chatbot, include a chatbot ID:</p>

          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">{`chatco-mcp login \\
  --name "Claude Desktop" \\
  --chatbot-id YOUR_CHATBOT_ID`}</pre>
          </div>

          <p>
            To customize permissions or expiry, you can also provide scopes and an API key lifetime:
          </p>

          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">{`chatco-mcp login \\
  --name "Cursor" \\
  --scopes "bot:read,bot:write,content:read,content:write" \\
  --api-key-expires-in-days 30`}</pre>
          </div>

          <div className="my-6 rounded-lg border border-blue-200 bg-blue-50 p-5">
            <p className="text-sm text-blue-900">
              The login command prints an approval URL and short code, then waits for approval. You
              do not have to manually copy a raw API key into the client.
            </p>
          </div>
        </section>

        <section id="approve-access" className="scroll-mt-24">
          <h2>4. Approve Access in Chat.co</h2>

          <p>
            Open the URL shown by the login command. If you are not already signed in, Chat.co will
            ask you to sign in first.
          </p>

          <h3>On the approval screen, review:</h3>
          <ul>
            <li>The requesting client name</li>
            <li>The requested scopes</li>
            <li>Whether the request is scoped to one chatbot or left unscoped</li>
            <li>The API key lifetime</li>
            <li>The device approval expiry window</li>
          </ul>

          <h3>Then choose:</h3>
          <ul>
            <li>
              <strong>Approve</strong> to issue the MCP key
            </li>
            <li>
              <strong>Deny</strong> if the request is broader than you intended
            </li>
          </ul>

          <p>
            After approval, the login command finishes the exchange and writes the issued key to a
            local file for the MCP server to use.
          </p>
        </section>

        <section id="configure-client" className="scroll-mt-24">
          <h2>5. Configure Your MCP Client</h2>

          <p>
            Most MCP clients use a configuration entry that launches a local stdio server. The exact
            file location depends on your client, but the structure usually looks like this:
          </p>

          <p>
            If you installed globally with <code>npm install -g chatco-mcp@1.0.0</code>:
          </p>

          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">{`{
  "mcpServers": {
    "chatco": {
      "type": "stdio",
      "command": "chatco-mcp",
      "env": {
        "CHATCO_API_URL": "https://api.chat.co",
        "CHATCO_API_KEY_FILE": "~/.config/chatco/mcp.key",
        "CHATCO_TIMEOUT_MS": "120000"
      }
    }
  }
}`}</pre>
          </div>

          <p>
            If you prefer to run via <code>npx</code> without a global install, use this form
            instead:
          </p>

          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">{`{
  "mcpServers": {
    "chatco": {
      "type": "stdio",
      "command": "npx",
      "args": ["-y", "chatco-mcp@1.0.0"],
      "env": {
        "CHATCO_API_URL": "https://api.chat.co",
        "CHATCO_API_KEY_FILE": "~/.config/chatco/mcp.key",
        "CHATCO_TIMEOUT_MS": "120000"
      }
    }
  }
}`}</pre>
          </div>

          <p>
            If your installation runs the Chat.co MCP server differently, keep the same environment
            variables and replace the command with the one provided by your installer or support
            team.
          </p>

          <h3>What these settings do</h3>
          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Setting
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Purpose
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                    CHATCO_API_URL
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Points the MCP server at your Chat.co API environment
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                    CHATCO_API_KEY_FILE
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Tells the MCP server where to read the approved key
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                    CHATCO_TIMEOUT_MS
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Optional request timeout for longer-running operations
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section id="verify-connection" className="scroll-mt-24">
          <h2>6. Verify the Connection</h2>

          <p>
            Once your MCP client is configured, confirm the integration with a few safe read
            operations:
          </p>

          <ol>
            <li>Ask the client to list your chatbots</li>
            <li>Ask it to read one chatbot&apos;s configuration</li>
            <li>Ask it to summarize available tools or describe what it can access</li>
          </ol>

          <div className="my-6 rounded-lg border border-gray-200 bg-white p-5">
            <h4 className="font-semibold text-gray-900">Good first prompts</h4>
            <ul className="mt-3 text-sm text-gray-600">
              <li>&quot;List my Chat.co bots.&quot;</li>
              <li>&quot;Show me the configuration for my support bot.&quot;</li>
              <li>&quot;Which Chat.co tools are available to you right now?&quot;</li>
            </ul>
          </div>

          <p>
            If those work, you can move on to write operations like updating prompts, uploading
            content, or reviewing analytics.
          </p>
        </section>

        <section id="scope-security" className="scroll-mt-24">
          <h2>7. Scope & Security Choices</h2>

          <p>MCP is powerful, so it should be configured intentionally.</p>

          <h3>Recommended approach</h3>
          <ol>
            <li>1. Start with the smallest scope that fits the workflow</li>
            <li>2. Use a chatbot-scoped connection for single-bot operations</li>
            <li>
              3. Use unscoped access only when you truly need multi-bot or account-level actions
            </li>
            <li>4. Set a reasonable expiry for the approved key</li>
            <li>5. Rotate or revoke the connection when the operator or workflow changes</li>
          </ol>

          <div className="my-6 rounded-xl border border-amber-200 bg-amber-50 p-6">
            <h4 className="font-semibold text-amber-900">Security reminder</h4>
            <p className="mt-2 text-amber-800">
              The MCP server runs locally and reads a local key file. Treat that file like any other
              credential. Do not share it, commit it to version control, or place it in broadly
              accessible folders.
            </p>
          </div>
        </section>

        <section id="troubleshooting" className="scroll-mt-24">
          <h2>8. Troubleshooting</h2>

          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">The login command keeps waiting</h4>
              <p className="mt-2 text-sm text-gray-600">
                Open the approval URL, make sure you are signed in to Chat.co, and confirm the
                request has not expired.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">
                The client connects but cannot do much
              </h4>
              <p className="mt-2 text-sm text-gray-600">
                The approved key may be too narrow or scoped to a single chatbot. Re-run login with
                the scopes you need.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Account-wide analytics are missing</h4>
              <p className="mt-2 text-sm text-gray-600">
                You likely used a chatbot-scoped key. Reconnect without a chatbot ID if you need
                account-level operations.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Document upload fails</h4>
              <p className="mt-2 text-sm text-gray-600">
                Make sure the file path is local, readable by the MCP server, and that your bot has
                the required knowledge base access and available storage credits.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">The connection stops working later</h4>
              <p className="mt-2 text-sm text-gray-600">
                The approved key may have expired. Run agent login again to renew it.
              </p>
            </div>
          </div>

          <p>
            For broader integration planning, read{' '}
            <Link
              href={PageLink.docsApiVsMcp}
              className="font-medium text-primary-600 hover:underline">
              API vs MCP
            </Link>
            . For raw HTTP integrations, continue to the{' '}
            <Link
              href={PageLink.docsApiReference}
              className="font-medium text-primary-600 hover:underline">
              API documentation
            </Link>
            . For a start-to-finish example, read{' '}
            <Link
              href={PageLink.docsPrivateAppChatbotMcp}
              className="font-medium text-primary-600 hover:underline">
              Build a Private-App Chatbot with MCP in 30 Minutes
            </Link>
            .
          </p>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export default McpAgentSetupPage;
